/**
 * interior_descriptor.ts — WHAT A PLANNED INTERIOR IS (the interiors programme, slice B, 2026-10-01). PURE: no three,
 * no DOM, no imports — node-testable and mirrorable on the server (wave 3).
 *
 * `planInterior` (interior_planner.ts) returns an `InteriorDescriptor`; the visit (slice C) renders it; the venue
 * binding (slice E) reads its anchors; the server (slice G) will compare its DIGEST. It carries, per floor:
 *
 *   · pieces  — what stands where, in PRIORITY order (the pack prefix law: the first n pieces of a floor are a valid
 *               room on their own). Each has a STABLE SLOT ID `<zone>/<kit>/<role>#n` that survives re-ordering — the
 *               legacy plan's slots are `legacy/<floor>/<index>`;
 *   · zones   — the functional regions (id, role, access as a POLICY REQUEST, rects);
 *   · anchors — where things are USED: the thing, the standing point (proven reachable by the real capsule), the
 *               facing, the use envelope, the seat socket;
 *   · routes  — the walking tree from the floor's entry to every zone and anchor;
 *   · units   — rentable rooms / dwellings / a home's spare room (`unit/f<floor>/<n>`, stable under packs and detail);
 * per building: `links` (each stair run and each lift ride between two floors' route nodes) and `liftCandidate` (the
 * shaft reserved on every floor of a ≥ 3-floor building); per piece the COVER fields (`blocksWalk`, `coverHeight`,
 * `blocksSight`) — pure data for the later slices (rentals, lifts, indoor combat); nothing here builds them;
 * and the RECIPE: every input that may change the plan (schema / layout / algorithm versions, the archetype and its
 * revision, the shell signature, the kit version, the catalog signature, the seed namespace, the search policy).
 *
 * THE DIGEST: one string two clients (and later the server) compare. Integer layout units (millimetres, yaw in
 * 1e-4 rad), canonical ordering (floors ascending, pieces in priority order, zones / anchors by id), a fixed hash
 * (four FNV-1a lanes, fmix32-finalised — `Math.imul` only). Routes are NOT in the digest: they are derived from what
 * is, and a nicer polyline must not read as a different room.
 *
 * SEEDS (`seedOf`): `buildingId | seedNamespace | floor | zone | stream` → uint32, FNV-1a + the murmur3 finalizer (THE
 * HASH LAW: bare FNV-1a is arithmetic in its last character). The building id is used EXACTLY as registered — the
 * doubled `settle:<site>:settle:<site>:<n>` prefix included; never normalise it. Streams are independent by
 * construction (`topology` · `primary` · `secondary` · `detail`): adding a vase cannot move the bed.
 */

export const DESCRIPTOR_SCHEMA = 1;

export type PieceTier = 'primary' | 'secondary' | 'detail' | 'owned';
/** A POLICY REQUEST only — who the zone is FOR. The server's ACL (structure_permission, post holders) decides. */
export type AccessRole = 'public' | 'owner' | 'employee' | 'guarded';
/** `room` is recorded and not built in wave 1 (walls need the shared-room phase, slice G). */
export type Enclosure = 'zone' | 'screen' | 'room';
export type AnchorKind =
  | 'service-staff' | 'service-customer' | 'queue' | 'seat' | 'bed' | 'bedside' | 'storage' | 'display' | 'perform' | 'stand'
  /** a resident's WORK point (a desk seat, a bench front) · a unit's door / THRESHOLD · a lift's CALL post */
  | 'work' | 'threshold' | 'lift-call';
export type PlanAlgorithm = 'planner' | 'legacy';
/** Which rung of the fallback ladder produced the room. */
export type PlanRung = 'full' | 'simpler' | 'sparse' | 'legacy' | 'empty';
/** planned = the archetype's own program · fallback = a lower rung · legacy = today's plan (asked for, or the archetype
 *  IS legacy) · empty = nothing safe fits (reasons say why) · conflict = owned content blocks a mandatory route: nothing
 *  was added, moved or deleted. */
export type PlanStatus = 'planned' | 'fallback' | 'legacy' | 'empty' | 'conflict';
export type StreamName = 'topology' | 'primary' | 'secondary' | 'detail';

/** An axis-aligned rect in building-local metres (x across, z toward the back wall). */
export interface Rect { x0: number; z0: number; x1: number; z1: number }

export interface DescPiece {
  /** STABLE slot id: `<zone>/<kit>/<role>#n` (unique per floor); `legacy/<floor>/<index>`; an owned piece keeps the caller's id. */
  slot: string;
  /** Catalog key (`FURNITURE`). */
  key: string;
  floor: number;
  /** Position in the floor's PRIORITY order — the index a pack prefix cuts at (`packSlotCount`). */
  order: number;
  /** Building-local metres; yaw in radians, the plans' convention: 0 faces +Z (away from the door), π faces the door. */
  x: number; z: number; yaw: number;
  /** Metres above the floor's slab: 0 on the floor, the support's top for a piece standing on one. */
  lift: number;
  tier: PieceTier;
  zone: string; kit: string; role: string;
  /** Does it block walking (a rug and a lamp on a table do not). */
  blocksWalk: boolean;
  /** COVER (indoor combat, slice K): the height of what is DRAWN, metres above the piece's own base (`lift` + this =
   *  its top over the slab) — the served model's measured height where known, else the catalog bound (h × 1.5). */
  coverHeight: number;
  /** Does it block a line of sight at standing height: `coverHeight ≥ COVER_SIGHT_M` and the piece is not see-through. */
  blocksSight: boolean;
  /** Drawn half extents at yaw 0 (m): `drawnHalf(key, 0)` — the oriented bound every check used. */
  half: { w: number; d: number };
  /** The slot it stands on (a support relation). */
  on?: string;
  /** Slots that must be seated before it — every one has a LOWER `order` (the prefix law's dependency rule). */
  needs?: string[];
}

export interface DescZone {
  id: string; role: string; floor: number;
  access: AccessRole; enclosure: Enclosure; mandatory: boolean;
  rects: Rect[]; areaM2: number;
  /** The kit ids seated in it. */
  kits: string[];
  /** The rentable UNIT this zone is (slice R) — see `DescUnit`. */
  unitId?: string;
}

/** A RENTABLE UNIT (slice R): a hotel room, an apartment, a home's spare room. Its id is `unit/f<floor>/<n>` — n is the
 *  unit's place in the floor program's fixed order, so it never depends on what was seated (pack size, detail pieces). */
export interface DescUnit {
  unitId: string; floor: number;
  kind: 'room' | 'dwelling' | 'spare';
  /** The zone that is the unit. */
  zone: string;
  rect: Rect;
  /** The unit's door / threshold anchor id (kind `threshold`) — on the unit's circulation side. */
  threshold: string;
  /** The anchors INSIDE the unit (bed, storage, seat, work), by id. */
  anchors: string[];
  /** The slots of the pieces inside it. */
  pieces: string[];
}

/** A partition or screen (slices F / G build them; wave 1 emits none): a wall segment with its cover data. */
export interface DescPartition {
  id: string; floor: number;
  x0: number; z0: number; x1: number; z1: number;
  thickness: number; height: number;
  blocksWalk: boolean; coverHeight: number; blocksSight: boolean;
  /** Doorway apertures along it, metres from (x0, z0): [start, end]. */
  apertures: Array<[number, number]>;
}

/**
 * A ROOM (slice G, layout ≥ 2): a connected region of a floor bounded by the shell and the partitions, entered through
 * DOORWAYS. Its id is `room/f<floor>/<name>` — `main` is the region the floor's entry stands in (a floor with no
 * closed partition is ONE room, `main`), a rented unit's room is `unit<n>`, an enclosed zone's is the zone id, a big
 * floor's chambers are `hall<n>` in walking order from the entry. `rects` is a disjoint cover (the wall cells belong
 * to no room); (x, z) a standing point the capsule route reaches.
 */
export interface DescRoom {
  id: string; floor: number;
  /** The name part of the id (the zone, `unit<n>`, `main`, `hall<n>`). */
  zone: string;
  /** The rentable unit this room IS (slice R) — `unit/f<floor>/<n>`. */
  unitId?: string;
  access: AccessRole;
  rects: Rect[]; areaM2: number;
  x: number; z: number;
}
/** A DOORWAY: an open aperture of a partition joining two rooms — the only way between them. (ax, az) / (bx, bz) are
 *  the standing points either side (in rooms `a` / `b`), each reached by the capsule route with every wall solid. */
export interface DescDoorway {
  id: string; floor: number;
  /** The partition that carries the aperture. */
  partition: string;
  a: string; b: string;
  x: number; z: number;
  /** The clear width between the jambs (m). */
  width: number;
  /** The wall's direction: 'x' = it runs along x (you cross it walking ±z). */
  axis: 'x' | 'z';
  ax: number; az: number; bx: number; bz: number;
}

/** A VERTICAL LINK between two floors' route nodes (slice L): the stair run, or the reserved lift. */
export interface FloorLink {
  kind: 'stair' | 'lift';
  from: { floor: number; node: string };
  to: { floor: number; node: string };
  /** Walking cost in metres (the stair's run + landing; the lift's nominal ride). */
  costM: number;
  /** A lift is a CANDIDATE until `interiorSpec` builds the shaft (slice L). */
  candidate: boolean;
}

/** THE LIFT CANDIDATE (buildings of ≥ 3 floors): a shaft the plan keeps free on every floor — same x / z on each,
 *  against a wall, never in the door corridor or the stair zone. Reserved and described here; nothing is built. */
export interface LiftCandidate {
  rect: Rect;
  /** The wall it stands against and the side its door opens to. */
  wall: 'west' | 'north' | 'east';
  door: 'east' | 'south' | 'west';
  /** One call post per floor: where a rider stands (reachable on every PLANNED floor) and the anchor id there. */
  /** `clear`: no blocking piece stands in the shaft or its apron on that floor (always true on a PLANNED floor; a
   *  legacy floor or an owned piece may stand there — slice L must clear it before it builds). */
  calls: Array<{ floor: number; x: number; z: number; facing: number; anchor: string; reachable: boolean; clear: boolean }>;
}

export interface DescAnchor {
  /** `<zone>/<kit>/<kind>#n`. */
  id: string; zone: string; floor: number; kind: AnchorKind;
  /** The thing used (a piece's centre, a service point). */
  x: number; z: number;
  /** Where the user stands — a free cell the capsule route reaches. */
  ax: number; az: number;
  /** The yaw the user faces while using it (the plans' convention). */
  facing: number;
  /** The use envelope: an axis-aligned box centred on (ax, az) kept clear of every blocking piece. */
  envelope: { hx: number; hz: number };
  /** The piece it belongs to. */
  piece?: string;
  /** A seat / lie socket: where the body goes and how (`seatPose`). */
  seat?: { pose: 'sit' | 'lie'; eye: number; x: number; z: number; yaw: number };
  access: AccessRole;
  /** Walking distance from the floor's entry (m). */
  routeM: number;
}

export interface RouteNode { id: string; kind: 'entry' | 'landing' | 'stair-foot' | 'console' | 'lift-call' | 'zone' | 'anchor'; floor: number; x: number; z: number }
export interface RouteEdge { from: string; to: string; lengthM: number; points: Array<[number, number]> }

export interface DescFloor {
  floor: number;
  /** The slab y the visit seats pieces at: 0.08 on the ground floor, `floor · FLOOR_H` above. */
  floorY: number;
  /** The floor program's id (`home.ground`, `legacy.clinic.upper`, `unprogrammed`). */
  program: string;
  source: 'planned' | 'legacy' | 'empty';
  /** Usable walkable area after the shell, the stairs and the reservations (m²). */
  usableM2: number;
  pieces: DescPiece[];
  zones: DescZone[];
  anchors: DescAnchor[];
  /** Rentable units on this floor (may be empty). */
  units: DescUnit[];
  /** Partitions / screens on this floor (none in wave 1). */
  partitions: DescPartition[];
  /** The floor's rooms (slice G): always at least `room/f<floor>/main`; more only where partitions close a region. */
  rooms: DescRoom[];
  /** The doorways between them (empty on a one-room floor). */
  doorways: DescDoorway[];
  routeNodes: RouteNode[];
  routes: RouteEdge[];
}

export interface InteriorRecipe {
  schemaVersion: number;
  /** The layout a structure is PINNED to (slice C's `structure_layout`): 0 = today's legacy plan. */
  layoutRevision: number;
  algorithmVersion: string;
  algorithm: PlanAlgorithm;
  archetypeId: string; archetypeRevision: number;
  /** `w|h|d|key-class` quantised — everything `interiorSpec` reads. */
  shellSignature: string;
  kitVersion: string;
  /** Hash of every catalog piece's key and dims: a matching seed with a different catalog is not the same plan. */
  catalogSignature: string;
  seedNamespace: string;
  purpose: string;
  tradition: string;
  /** `law` = law 22 as written (the stair zone reserved in every shell); `shell` = only where the shell builds a run. */
  stairZone: 'law' | 'shell';
  /** A deed reserves the structure console's ground. */
  deed: boolean;
  /** The search policy: fixed WORK budgets, never wall-clock. */
  policy: { K: number; R: number; cellMm: number };
  /** Revision of the owned (player-placed) content the plan was made around ('' = none). */
  ownedRevision: string;
}

export interface InteriorDescriptor {
  buildingId: string;
  recipe: InteriorRecipe;
  status: PlanStatus;
  rung: PlanRung;
  /** Reason codes for the result (why a rung was left, why a zone dropped, why nothing fits). */
  reasons: string[];
  floors: DescFloor[];
  /** Vertical links between floors' route nodes: every stair run, and the lift candidate's rides. */
  links: FloorLink[];
  /** The reserved lift shaft (≥ 3 floors), or null. */
  liftCandidate: LiftCandidate | null;
  /** 32 hex chars over the canonical form (see the header). */
  digest: string;
}

/** THE ROOMS REVISION (slice G): from this layout revision on the planner BUILDS the enclosures the programs ask for
 *  (units, `enclosure: 'room' | 'screen'`, the division of a large floor) and the digest carries rooms and doorways.
 *  Below it a plan is bit-identical to what it was — a structure pinned at layout 1 keeps its open room. */
export const ROOMS_LAYOUT_REVISION = 2;
export const ROOMS_VERSION = 'rooms-1';
/** THE STOREYS REVISION (slice U, 2026-10-02): from this layout revision on EVERY storey of a building has a program
 *  (by the building's purpose and the storey's role — first upper, middle, top), a small storey falls to a compact
 *  program before it falls to the legacy plan, the legacy fallback keeps the lift's cell and its landing clear, and the
 *  landing's way past a back-wall shaft is seen by the route raster (THE LANDING GATE). Below it a plan is
 *  bit-identical to what it was — a structure pinned at layout 1 or 2 keeps its room. */
export const STOREYS_LAYOUT_REVISION = 3;
export const STOREYS_VERSION = 'storeys-1';
export const roomIdOf = (floor: number, name: string): string => `room/f${floor}/${name}`;

/** A piece at or above this drawn height blocks sight at standing height (unless see-through). */
export const COVER_SIGHT_M = 1.5;

// ─── The report (what the planner tried) — never part of the digest ─────────────────────────────────────────────────

export interface ReservedRegion { kind: 'door' | 'stair' | 'landing' | 'console' | 'lift' | 'owned' | 'shell' | 'approach'; floor: number; rect: Rect; label: string }
export interface CandidateReport {
  /** `<rung>:<archetype>:<arrangement>`. */
  id: string; rung: PlanRung;
  status: 'valid' | 'rejected';
  /** Why it was rejected, or what it gave up (dropped zones / kits) on the way to valid. */
  reasons: string[];
  /** Integer soft score (valid candidates only). */
  score: number;
  scoreParts: Record<string, number>;
  pieces: number;
  repairs: number;
}
export interface PlanReport {
  candidates: CandidateReport[];
  chosen: string;
  reserved: ReservedRegion[];
  /** Work counters — the budget in units of work, not time. */
  work: { candidates: number; repairs: number; bfs: number; cells: number; placements: number; tries: number };
  /** The hard validation of the RETURNED descriptor: every check and what failed (empty = clean). */
  hard: string[];
}

// ─── Hashes and seeds ────────────────────────────────────────────────────────────────────────────────────────────────

/** The murmur3 finalizer. */
function fmix32(h: number): number {
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}

/** FNV-1a over a string, fmix32-finalised (THE HASH LAW) → uint32. */
export function hash32(s: string, seed = 0x811c9dc5): number {
  let h = seed >>> 0;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return fmix32(h);
}

/** A stream's seed: the building id EXACTLY as registered + the recipe's namespace + floor + zone + stream. */
export function seedOf(buildingId: string, seedNamespace: string, floor: number, zone: string, stream: StreamName): number {
  return hash32(`${buildingId}|${seedNamespace}|${floor}|${zone}|${stream}`);
}

/** A tiny deterministic generator (mulberry32) — integers only on the way out. */
export interface Rng { next(): number; int(n: number): number; chance(num: number, den: number): boolean }
export function rngOf(seed: number): Rng {
  let a = seed >>> 0;
  const next = (): number => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (t ^ (t >>> 14)) >>> 0;
  };
  // `int` multiplies two uint32-range values through 16-bit halves — exact integers on every engine.
  const int = (n: number): number => {
    if (n <= 1) return 0;
    const u = next();
    return Math.floor(((u >>> 16) * n + (((u & 0xffff) * n) >>> 16)) / 65536) % n;
  };
  return { next, int, chance: (num, den) => int(den) < num };
}

/** Four FNV-1a lanes over a string → 32 hex chars. */
export function hash128Hex(s: string): string {
  const lanes = [0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35];
  let out = '';
  for (let k = 0; k < 4; k++) {
    let h = lanes[k] >>> 0;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i) + k; h = Math.imul(h, 0x01000193); h ^= h >>> 15; }
    out += fmix32(h ^ s.length).toString(16).padStart(8, '0');
  }
  return out;
}

// ─── The digest ──────────────────────────────────────────────────────────────────────────────────────────────────────

/** Metres → integer millimetres (the layout unit). */
export const mm = (v: number): number => Math.round(v * 1000);
/** Radians → integer 1e-4 rad, wrapped to [0, 2π). */
export function yawUnits(yaw: number): number {
  const TWO_PI_U = 62832; // round(2π · 1e4)
  let u = Math.round(yaw * 10000) % TWO_PI_U;
  if (u < 0) u += TWO_PI_U;
  return u === TWO_PI_U ? 0 : u;
}

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** The canonical form the digest hashes: every plan-affecting field, integers only, in a fixed order. */
type DigestInput = Pick<InteriorDescriptor, 'buildingId' | 'recipe' | 'status' | 'rung' | 'floors' | 'links' | 'liftCandidate'>;
const rectStr = (q: Rect): string => `${mm(q.x0)},${mm(q.z0)},${mm(q.x1)},${mm(q.z1)}`;
export function canonicalForm(d: DigestInput): string {
  const r = d.recipe;
  const parts: string[] = [
    `v${r.schemaVersion}`, d.buildingId, `L${r.layoutRevision}`, r.algorithm, r.algorithmVersion, `${r.archetypeId}@${r.archetypeRevision}`,
    r.shellSignature, r.kitVersion, r.catalogSignature, r.seedNamespace, r.purpose, r.tradition, r.stairZone, r.deed ? 'deed' : 'free',
    `K${r.policy.K}R${r.policy.R}c${r.policy.cellMm}`, `own:${r.ownedRevision}`, d.status, d.rung,
  ];
  for (const f of [...d.floors].sort((a, b) => a.floor - b.floor)) {
    parts.push(`F${f.floor}:${f.program}:${f.source}`);
    for (const p of [...f.pieces].sort((a, b) => a.order - b.order)) {
      parts.push(`p:${p.order}:${p.slot}:${p.key}:${mm(p.x)}:${mm(p.z)}:${yawUnits(p.yaw)}:${mm(p.lift)}:${p.tier}:${p.blocksWalk ? 1 : 0}:${mm(p.coverHeight)}:${p.blocksSight ? 1 : 0}:${p.on ?? ''}`);
    }
    for (const z of [...f.zones].sort((a, b) => cmp(a.id, b.id))) {
      parts.push(`z:${z.id}:${z.role}:${z.access}:${z.enclosure}:${z.mandatory ? 1 : 0}:${z.unitId ?? ''}:${z.rects.map(rectStr).join(';')}`);
    }
    for (const a of [...f.anchors].sort((a2, b2) => cmp(a2.id, b2.id))) {
      parts.push(`a:${a.id}:${a.kind}:${mm(a.x)}:${mm(a.z)}:${mm(a.ax)}:${mm(a.az)}:${yawUnits(a.facing)}:${mm(a.envelope.hx)}:${mm(a.envelope.hz)}:${a.piece ?? ''}:${a.seat ? `${a.seat.pose}@${mm(a.seat.eye)}` : ''}:${a.access}`);
    }
    for (const u of [...f.units].sort((a2, b2) => cmp(a2.unitId, b2.unitId))) {
      parts.push(`u:${u.unitId}:${u.kind}:${u.zone}:${rectStr(u.rect)}:${u.threshold}:${[...u.anchors].sort(cmp).join(',')}`);
    }
    for (const w of [...f.partitions].sort((a2, b2) => cmp(a2.id, b2.id))) {
      parts.push(`w:${w.id}:${mm(w.x0)},${mm(w.z0)},${mm(w.x1)},${mm(w.z1)}:${mm(w.thickness)}:${mm(w.height)}:${w.blocksWalk ? 1 : 0}:${mm(w.coverHeight)}:${w.blocksSight ? 1 : 0}:${w.apertures.map((q) => `${mm(q[0])}-${mm(q[1])}`).join(';')}`);
    }
    // (slice G) rooms and doorways are part of the plan from THE ROOMS REVISION on — never below it (layout 0 / 1 digests are what they were)
    if (r.layoutRevision >= ROOMS_LAYOUT_REVISION) {
      for (const q of [...(f.rooms ?? [])].sort((a2, b2) => cmp(a2.id, b2.id))) parts.push(`r:${q.id}:${q.unitId ?? ''}:${q.access}:${q.rects.map(rectStr).join(';')}`);
      for (const q of [...(f.doorways ?? [])].sort((a2, b2) => cmp(a2.id, b2.id))) parts.push(`d:${q.id}:${q.partition}:${q.a}:${q.b}:${mm(q.x)},${mm(q.z)}:${mm(q.width)}:${q.axis}`);
    }
  }
  if (r.layoutRevision >= ROOMS_LAYOUT_REVISION) parts.push(ROOMS_VERSION);
  // (slice U) the storeys revision's rules are part of the recipe from that revision on — never below it
  if (r.layoutRevision >= STOREYS_LAYOUT_REVISION) parts.push(STOREYS_VERSION);
  for (const l of [...d.links].sort((a, b) => cmp(`${a.kind}${a.from.floor}`, `${b.kind}${b.from.floor}`))) {
    parts.push(`l:${l.kind}:${l.from.floor}:${l.from.node}:${l.to.floor}:${l.to.node}:${mm(l.costM)}:${l.candidate ? 1 : 0}`);
  }
  if (d.liftCandidate) {
    const lc = d.liftCandidate;
    parts.push(`lift:${rectStr(lc.rect)}:${lc.wall}:${lc.door}:${lc.calls.map((c) => `${c.floor}@${mm(c.x)},${mm(c.z)},${yawUnits(c.facing)},${c.reachable ? 1 : 0}${c.clear ? 1 : 0}`).join(';')}`);
  }
  return parts.join('\n');
}

export function descriptorDigest(d: DigestInput): string {
  return hash128Hex(canonicalForm(d));
}

// ─── Small readers slice C / E build on ──────────────────────────────────────────────────────────────────────────────

/** The floor's pieces as the legacy `PlanSlot` list (`{ key, x, z, yaw }`) in priority order — what `placeSlot` takes. */
export function floorSlots(f: DescFloor): Array<{ key: string; x: number; z: number; yaw: number }> {
  return [...f.pieces].filter((p) => p.tier !== 'owned').sort((a, b) => a.order - b.order).map((p) => ({ key: p.key, x: p.x, z: p.z, yaw: p.yaw }));
}

/** The first `n` GENERATED pieces of a floor in priority order — what a furnish pack seats (`packSlotCount`). */
export function floorPrefix(f: DescFloor, n: number): DescPiece[] {
  return [...f.pieces].filter((p) => p.tier !== 'owned').sort((a, b) => a.order - b.order).slice(0, Math.max(0, n));
}

/** Every anchor of a kind, ground floor first, nearest the entry first — the venue binding's reader. */
export function anchorsOfKind(d: InteriorDescriptor, kind: AnchorKind): DescAnchor[] {
  const out: DescAnchor[] = [];
  for (const f of [...d.floors].sort((a, b) => a.floor - b.floor)) out.push(...f.anchors.filter((a) => a.kind === kind).sort((a, b) => a.routeM - b.routeM || cmp(a.id, b.id)));
  return out;
}

// ─── Rooms (slice G) — the readers the visit, the server's presence rule and slices R / K build on ───────────────────

/** The room a building-local point of a floor stands in; a point inside a wall (or off the floor) takes the NEAREST
 *  room within `slackM` (a wall is 0.4 m thick — a body touching it is still in its room), else null. */
export function roomAt(f: Pick<DescFloor, 'rooms'>, x: number, z: number, slackM = 0.6): DescRoom | null {
  let best: DescRoom | null = null, bestD = Infinity;
  for (const room of f.rooms) {
    for (const q of room.rects) {
      const dx = Math.max(q.x0 - x, 0, x - q.x1), dz = Math.max(q.z0 - z, 0, z - q.z1);
      if (dx === 0 && dz === 0) return room;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD) { bestD = d2; best = room; }
    }
  }
  return best && bestD <= slackM * slackM ? best : null;
}
export function floorOf(d: Pick<InteriorDescriptor, 'floors'>, floor: number): DescFloor | null {
  return d.floors.find((f) => f.floor === floor) ?? null;
}
export function roomById(d: Pick<InteriorDescriptor, 'floors'>, roomId: string): DescRoom | null {
  for (const f of d.floors) for (const r of f.rooms) if (r.id === roomId) return r;
  return null;
}
/** The rentable unit a room is (`unit/f<floor>/<n>`), or null — slice R's "the tenant's door". */
export function unitOfRoom(d: Pick<InteriorDescriptor, 'floors'>, roomId: string): string | null {
  return roomById(d, roomId)?.unitId ?? null;
}
/** The room that IS a unit, or null (the unit is a zone of an open floor — nothing encloses it at this layout). */
export function roomOfUnit(d: Pick<InteriorDescriptor, 'floors'>, unitId: string): DescRoom | null {
  for (const f of d.floors) for (const r of f.rooms) if (r.unitId === unitId) return r;
  return null;
}
/** The doorways of a room. */
export function doorwaysOf(f: Pick<DescFloor, 'doorways'>, roomId: string): DescDoorway[] {
  return f.doorways.filter((q) => q.a === roomId || q.b === roomId);
}
/** The solid boxes of a partition (the wall less its apertures), building-local metres — what the visit builds, what
 *  the route raster blocks, what the server stamps. */
export function partitionBoxes(w: DescPartition): Array<{ x0: number; z0: number; x1: number; z1: number }> {
  const alongX = Math.abs(w.x1 - w.x0) >= Math.abs(w.z1 - w.z0);
  const len = alongX ? w.x1 - w.x0 : w.z1 - w.z0, h = w.thickness / 2;
  const cuts = [...w.apertures].sort((a, b) => a[0] - b[0]);
  const out: Array<{ x0: number; z0: number; x1: number; z1: number }> = [];
  const seg = (s0: number, s1: number): void => {
    if (s1 - s0 < 0.01) return;
    out.push(alongX ? { x0: w.x0 + s0, x1: w.x0 + s1, z0: w.z0 - h, z1: w.z0 + h } : { x0: w.x0 - h, x1: w.x0 + h, z0: w.z0 + s0, z1: w.z0 + s1 });
  };
  let from = 0;
  for (const [a0, a1] of cuts) { seg(from, a0); from = Math.max(from, a1); }
  seg(from, len);
  return out;
}

/** The four corners of a piece's drawn oriented bound (building-local metres). */
export function pieceCorners(p: Pick<DescPiece, 'x' | 'z' | 'yaw' | 'half'>): Array<[number, number]> {
  const c = Math.cos(p.yaw), s = Math.sin(p.yaw);
  const out: Array<[number, number]> = [];
  for (const [lx, lz] of [[-p.half.w, -p.half.d], [p.half.w, -p.half.d], [p.half.w, p.half.d], [-p.half.w, p.half.d]] as const) {
    out.push([p.x + lx * c + lz * s, p.z - lx * s + lz * c]); // three's rotation.y — the visit's toWorld
  }
  return out;
}

// ─── The debug view (report §4.3 "Debug view") — pure data; a bed or the visit draws it ─────────────────────────────

export interface DebugFloor {
  floor: number; floorY: number; program: string;
  reserved: ReservedRegion[];
  zones: Array<{ id: string; role: string; access: AccessRole; enclosure: Enclosure; mandatory: boolean; rects: Rect[] }>;
  occupied: Array<{ slot: string; key: string; corners: Array<[number, number]>; tier: PieceTier; owned: boolean; blocksWalk: boolean; coverHeight: number; blocksSight: boolean; order: number }>;
  units: Array<{ unitId: string; kind: string; rect: Rect; threshold: string }>;
  partitions: DescPartition[];
  rooms: DescRoom[];
  doorways: DescDoorway[];
  sockets: Array<{ id: string; kind: AnchorKind; x: number; z: number; ax: number; az: number; facing: number; envelope: { hx: number; hz: number }; seat: boolean; access: AccessRole }>;
  routes: Array<{ from: string; to: string; lengthM: number; points: Array<[number, number]> }>;
  nodes: RouteNode[];
}
export interface DebugView {
  buildingId: string; digest: string; status: PlanStatus; rung: PlanRung;
  recipe: string;
  /** Local axes: +X across (east), +Z toward the back wall; the door is on −Z, the stairs climb the −X wall. */
  axes: { origin: [number, number]; x: [number, number]; z: [number, number] };
  shell: { hw: number; hd: number; floors: number; core: Rect; door: { x: number; z: number; halfW: number } };
  floors: DebugFloor[];
  links: FloorLink[];
  lift: LiftCandidate | null;
  /** The losing (and winning) candidates with their reasons. */
  candidates: Array<{ id: string; status: 'valid' | 'rejected'; score: number; reasons: string[] }>;
  reasons: string[];
  hard: string[];
}

export function interiorDebugView(d: InteriorDescriptor, report: PlanReport | null, shell: { hw: number; hd: number; floors: number }): DebugView {
  return {
    buildingId: d.buildingId, digest: d.digest, status: d.status, rung: d.rung,
    recipe: `${d.recipe.archetypeId}@${d.recipe.archetypeRevision} · ${d.recipe.algorithm} ${d.recipe.algorithmVersion} · kits ${d.recipe.kitVersion} · layout ${d.recipe.layoutRevision} · ${d.recipe.tradition} · stairs:${d.recipe.stairZone}`,
    axes: { origin: [0, 0], x: [1, 0], z: [0, 1] },
    shell: { hw: shell.hw, hd: shell.hd, floors: shell.floors, core: { x0: -shell.hw, z0: -shell.hd, x1: shell.hw, z1: shell.hd }, door: { x: 0, z: -shell.hd, halfW: 0.95 } },
    floors: d.floors.map((f) => ({
      floor: f.floor, floorY: f.floorY, program: f.program,
      reserved: (report?.reserved ?? []).filter((r) => r.floor === f.floor),
      zones: f.zones.map((z) => ({ id: z.id, role: z.role, access: z.access, enclosure: z.enclosure, mandatory: z.mandatory, rects: z.rects })),
      occupied: f.pieces.map((p) => ({ slot: p.slot, key: p.key, corners: pieceCorners(p), tier: p.tier, owned: p.tier === 'owned', blocksWalk: p.blocksWalk, coverHeight: p.coverHeight, blocksSight: p.blocksSight, order: p.order })),
      units: f.units.map((u) => ({ unitId: u.unitId, kind: u.kind, rect: u.rect, threshold: u.threshold })),
      partitions: f.partitions,
      rooms: f.rooms ?? [], doorways: f.doorways ?? [],
      sockets: f.anchors.map((a) => ({ id: a.id, kind: a.kind, x: a.x, z: a.z, ax: a.ax, az: a.az, facing: a.facing, envelope: a.envelope, seat: !!a.seat, access: a.access })),
      routes: f.routes.map((r) => ({ from: r.from, to: r.to, lengthM: r.lengthM, points: r.points })),
      nodes: f.routeNodes,
    })),
    links: d.links, lift: d.liftCandidate,
    candidates: (report?.candidates ?? []).map((c) => ({ id: c.id, status: c.status, score: c.score, reasons: c.reasons })),
    reasons: d.reasons,
    hard: report?.hard ?? [],
  };
}
